import embeddedRuleSetJson from "../config/model-aliases.json";
import { FormControlValueReplacer } from "./form-control-replacer";

declare const __PACKAGE_VERSION__: string;
declare const __INJECTED_RULES_BASE_URL__: string;

export const DEFAULT_CMS_BASE_URL = "https://api-cms.paigod.work";
export const DEFAULT_RULES_BASE_URL =
  `${DEFAULT_CMS_BASE_URL}/api/items/model_replace_v1?page=1&limit=10`;
export const DEFAULT_OPEN_STATUS_URL =
  `${DEFAULT_CMS_BASE_URL}/api/items/open_model_replace/1`;
const RULES_CACHE_KEY = "model-replace:remote-rules:v1";
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 5_000;

export interface ModelReplaceRuleScope {
  excludeDomains: string[];
  includePagePaths: string[];
}

export interface ModelReplaceRuleSet {
  version: string;
  enabled?: boolean;
  rules: Record<string, string>;
  scopes?: Record<string, ModelReplaceRuleScope>;
}

export interface ModelReplaceStartOptions {
  ruleSet?: ModelReplaceRuleSet;
  baseUrl?: string;
  rulesUrl?: string;
  root?: Node;
  observe?: boolean;
  formControls?: boolean;
  ignoreSelector?: string;
  debug?: boolean;
  cacheTtlMs?: number;
}

export interface ModelReplaceStatus {
  active: boolean;
  isOpen: boolean;
  observe: boolean;
  packageVersion: string;
  rulesVersion: string;
  ruleCount: number;
  replacementCount: number;
  controlReplacementCount: number;
}

const DEFAULT_IGNORE_SELECTOR = [
  "script",
  "style",
  "noscript",
  "textarea",
  "input",
  "select",
  "[contenteditable='true']",
  "[data-model-replace-ignore]",
].join(", ");

const embeddedRuleSet = embeddedRuleSetJson as ModelReplaceRuleSet;

interface CompiledRule {
  source: string;
  target: string;
  scope?: ModelReplaceRuleScope;
}

interface CachedRuleSet {
  requestedUrl: string;
  cachedAt: number;
  ruleSet: ModelReplaceRuleSet;
}

interface RemoteEndpoints {
  rulesUrl: string;
  openStatusUrl: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function isCasedLetter(value: string): boolean {
  return value.toLowerCase() !== value.toUpperCase();
}

function applyMatchedCase(match: string, replacement: string): string {
  const letters = [...match].filter(isCasedLetter);
  if (letters.length === 0) return replacement;

  if (letters.every((letter) => letter === letter.toUpperCase())) {
    return replacement.toUpperCase();
  }
  if (letters.every((letter) => letter === letter.toLowerCase())) {
    return replacement.toLowerCase();
  }

  const [firstLetter, ...remainingLetters] = letters;
  if (
    firstLetter === firstLetter.toUpperCase() &&
    remainingLetters.every((letter) => letter === letter.toLowerCase())
  ) {
    let capitalized = false;
    return [...replacement]
      .map((letter) => {
        if (!isCasedLetter(letter)) return letter;
        if (!capitalized) {
          capitalized = true;
          return letter.toUpperCase();
        }
        return letter.toLowerCase();
      })
      .join("");
  }

  const matchCharacters = [...match];
  return [...replacement]
    .map((letter, index) => {
      const matchedLetter = matchCharacters[index];
      if (
        !matchedLetter ||
        !isCasedLetter(letter) ||
        !isCasedLetter(matchedLetter)
      ) {
        return letter;
      }
      return matchedLetter === matchedLetter.toUpperCase()
        ? letter.toUpperCase()
        : letter.toLowerCase();
    })
    .join("");
}

function splitCommaSeparated(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(/[,，]/u)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeDomain(value: string): string {
  const candidate = value.replace(/^\*\./u, "").replace(/^\./u, "");
  try {
    const url = new URL(
      candidate.includes("://") ? candidate : `http://${candidate}`,
    );
    return url.hostname.toLowerCase().replace(/\.$/u, "");
  } catch {
    return candidate.toLowerCase().replace(/\.$/u, "");
  }
}

function normalizeScope(value: unknown): ModelReplaceRuleScope {
  if (!value || typeof value !== "object") {
    return { excludeDomains: [], includePagePaths: [] };
  }
  const candidate = value as Partial<ModelReplaceRuleScope>;
  return {
    excludeDomains: Array.isArray(candidate.excludeDomains)
      ? candidate.excludeDomains
          .filter((item): item is string => typeof item === "string")
          .map((item) => normalizeDomain(item.trim()))
          .filter(Boolean)
      : [],
    includePagePaths: Array.isArray(candidate.includePagePaths)
      ? candidate.includePagePaths
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean)
      : [],
  };
}

function normalizeRuleSet(value: unknown): ModelReplaceRuleSet {
  if (!value || typeof value !== "object") {
    throw new Error("ModelReplace rules must be an object");
  }

  const candidate = value as Partial<ModelReplaceRuleSet>;
  if (!candidate.version || !candidate.rules || typeof candidate.rules !== "object") {
    throw new Error("ModelReplace rules require version and rules fields");
  }

  const rules = Object.fromEntries(
    Object.entries(candidate.rules).filter(
      ([source, target]) => source.length > 0 && typeof target === "string",
    ),
  );
  const candidateScopes = candidate.scopes ?? {};
  const scopes = Object.fromEntries(
    Object.keys(rules).map((source) => [
      source,
      normalizeScope(candidateScopes[source]),
    ]),
  );

  return {
    version: candidate.version,
    enabled: candidate.enabled !== false,
    rules,
    scopes,
  };
}

class DomModelReplacer {
  private ruleSet = normalizeRuleSet(embeddedRuleSet);
  private dictionary: Record<string, CompiledRule> = {};
  private activeDictionary: Record<string, CompiledRule> = {};
  private compiled: RegExp | null = null;
  private locationKey: string | null = null;
  private ignoreSelector = DEFAULT_IGNORE_SELECTOR;
  private observers = new Map<Node, MutationObserver>();
  private root: Node | null = null;
  private observe = true;
  private isOpen = true;
  private debug = false;
  private replacementCount = 0;
  private readonly formControlReplacer = new FormControlValueReplacer(
    (value) => this.replaceText(value),
  );

  constructor() {
    this.compile();
  }

  configure(
    ruleSet: ModelReplaceRuleSet,
    options: Pick<
      ModelReplaceStartOptions,
      "ignoreSelector" | "observe" | "formControls" | "debug"
    > = {},
  ): void {
    this.ruleSet = normalizeRuleSet(ruleSet);
    this.ignoreSelector = options.ignoreSelector || DEFAULT_IGNORE_SELECTOR;
    this.observe = options.observe !== false;
    this.debug = options.debug === true;
    this.formControlReplacer.configure(options.formControls !== false);
    this.compile();
  }

  updateRuleSet(ruleSet: ModelReplaceRuleSet): void {
    this.ruleSet = normalizeRuleSet(ruleSet);
    this.compile();
  }

  setOpen(value: boolean): void {
    this.isOpen = value;
  }

  private compile(): void {
    this.dictionary = Object.fromEntries(
      Object.entries(this.ruleSet.rules).map(([source, target]) => [
        source.toLowerCase(),
        { source, target, scope: this.ruleSet.scopes?.[source] },
      ]),
    );
    this.locationKey = null;
    this.refreshCompiledRules();
  }

  private pageContext(): {
    hostname: string;
    rawPathname: string;
    decodedPathname: string;
  } {
    const documentRef =
      typeof Document !== "undefined" && this.root instanceof Document
        ? this.root
        : this.root?.ownerDocument;
    const locationRef =
      documentRef?.location ??
      (typeof window !== "undefined" ? window.location : undefined);
    const hostname = locationRef?.hostname.toLowerCase() ?? "";
    const rawPathname = locationRef?.pathname || "/";
    let decodedPathname = rawPathname;
    try {
      decodedPathname = decodeURIComponent(rawPathname);
    } catch {
      // Keep the raw pathname when it contains malformed escape sequences.
    }
    return { hostname, rawPathname, decodedPathname };
  }

  private isRuleActive(
    rule: CompiledRule,
    context: ReturnType<DomModelReplacer["pageContext"]>,
  ): boolean {
    const scope = rule.scope;
    if (!scope) return true;

    const excluded = scope.excludeDomains.some(
      (domain) =>
        context.hostname === domain || context.hostname.endsWith(`.${domain}`),
    );
    if (excluded) return false;

    return (
      scope.includePagePaths.length === 0 ||
      scope.includePagePaths.some(
        (path) =>
          context.rawPathname.includes(path) ||
          context.decodedPathname.includes(path),
      )
    );
  }

  private refreshCompiledRules(): void {
    const context = this.pageContext();
    const nextLocationKey = `${context.hostname}\n${context.rawPathname}`;
    if (this.locationKey === nextLocationKey) return;

    this.locationKey = nextLocationKey;
    this.activeDictionary = Object.fromEntries(
      Object.entries(this.dictionary).filter(([, rule]) =>
        this.isRuleActive(rule, context),
      ),
    );
    const sources = Object.keys(this.activeDictionary)
      .filter(Boolean)
      .sort((left, right) => right.length - left.length)
      .map(escapeRegExp);
    this.compiled = sources.length > 0
      ? new RegExp(sources.join("|"), "giu")
      : null;
  }

  replaceText(value: string): string {
    this.refreshCompiledRules();
    if (!this.isOpen || !this.ruleSet.enabled || !this.compiled || !value) {
      return value;
    }
    return value.replace(this.compiled, (match) => {
      const rule = this.activeDictionary[match.toLowerCase()];
      if (!rule) return match;
      return match === rule.source
        ? rule.target
        : applyMatchedCase(match, rule.target);
    });
  }

  private shouldIgnore(node: Text): boolean {
    const parent = node.parentElement;
    return !parent || parent.closest(this.ignoreSelector) !== null;
  }

  private replaceTextNode(node: Text): number {
    if (this.shouldIgnore(node) || !node.nodeValue?.trim()) return 0;
    const nextValue = this.replaceText(node.nodeValue);
    if (nextValue === node.nodeValue) return 0;
    node.nodeValue = nextValue;
    this.replacementCount += 1;
    return 1;
  }

  private scanNode(root: Node): number {
    this.refreshCompiledRules();
    if (!this.isOpen || !this.ruleSet.enabled || !this.compiled) return 0;
    if (root.nodeType === Node.TEXT_NODE) {
      return this.replaceTextNode(root as Text);
    }

    if (
      root.nodeType !== Node.DOCUMENT_NODE &&
      root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE &&
      root.nodeType !== Node.ELEMENT_NODE
    ) {
      return 0;
    }

    const documentRef = root.ownerDocument ?? (root as Document);
    const walker = documentRef.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let replacements = 0;
    let node: Node | null;
    while ((node = walker.nextNode())) {
      replacements += this.replaceTextNode(node as Text);
    }

    replacements += this.formControlReplacer.scan(root);

    this.scanOpenShadowRoots(root);
    return replacements;
  }

  private scanOpenShadowRoots(root: Node): void {
    if (root instanceof Element && root.shadowRoot) {
      this.scanNode(root.shadowRoot);
      this.observeRoot(root.shadowRoot);
    }

    if (!(root instanceof Document || root instanceof DocumentFragment || root instanceof Element)) {
      return;
    }

    for (const element of root.querySelectorAll("*")) {
      if (element.shadowRoot) {
        this.scanNode(element.shadowRoot);
        this.observeRoot(element.shadowRoot);
      }
    }
  }

  private observeRoot(root: Node): void {
    if (!this.observe || this.observers.has(root)) return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        this.formControlReplacer.handleMutation(mutation);
        if (mutation.type === "characterData") {
          this.replaceTextNode(mutation.target as Text);
        }
        for (const node of mutation.addedNodes) this.scanNode(node);
      }
    });
    observer.observe(root, {
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        "class",
        "disabled",
        "hidden",
        "label",
        "placeholder",
        "readonly",
        "style",
        "type",
        "value",
        "data-model-replace-ignore",
      ],
      subtree: true,
    });
    this.observers.set(root, observer);
  }

  start(root: Node = document): number {
    this.stop();
    this.root = root;
    this.replacementCount = 0;
    this.formControlReplacer.start();
    const replacements = this.scanNode(root);
    this.observeRoot(root);
    if (this.debug) {
      console.info("[ModelReplace] started", this.status());
    }
    return replacements;
  }

  stop(): void {
    for (const observer of this.observers.values()) observer.disconnect();
    this.observers.clear();
    this.formControlReplacer.stop();
    this.root = null;
  }

  refresh(): number {
    if (!this.root) return 0;
    return this.scanNode(this.root);
  }

  status(): ModelReplaceStatus {
    return {
      active: this.root !== null,
      isOpen: this.isOpen,
      observe: this.observe,
      packageVersion: __PACKAGE_VERSION__,
      rulesVersion: this.ruleSet.version,
      ruleCount: Object.keys(this.ruleSet.rules).length,
      replacementCount: this.replacementCount,
      controlReplacementCount: this.formControlReplacer.replacedCount,
    };
  }
}

const runtime = new DomModelReplacer();
let startGeneration = 0;

function parseRemoteRuleSet(value: unknown): ModelReplaceRuleSet {
  if (!value || typeof value !== "object") {
    throw new Error("ModelReplace remote rules must be an object");
  }

  const payload = value as {
    data?: Array<{
      status?: boolean | number | string;
      goal_keyword?: unknown;
      replace_keyword?: unknown;
      exclude_domain?: unknown;
      include_page_paths?: unknown;
    }>;
    meta?: { collectionVersion?: string | number };
  };
  if (!Array.isArray(payload.data)) return normalizeRuleSet(value);

  const rules: Record<string, string> = {};
  const scopes: Record<string, ModelReplaceRuleScope> = {};
  for (const item of payload.data) {
    if (
      !item ||
      item.status === false ||
      item.status === 0 ||
      item.status === "0"
    ) {
      continue;
    }

    const source =
      typeof item.goal_keyword === "string" ? item.goal_keyword.trim() : "";
    const target =
      typeof item.replace_keyword === "string"
        ? item.replace_keyword.trim()
        : "";
    if (!source || !target) continue;

    rules[source] = target;
    scopes[source] = {
      excludeDomains: splitCommaSeparated(item.exclude_domain)
        .map(normalizeDomain)
        .filter(Boolean),
      includePagePaths: splitCommaSeparated(item.include_page_paths),
    };
  }

  if (Object.keys(rules).length === 0) {
    throw new Error("ModelReplace remote rules contain no active entries");
  }

  return normalizeRuleSet({
    version: String(payload.meta?.collectionVersion ?? "remote"),
    enabled: true,
    rules,
    scopes,
  });
}

async function requestRuleSet(rulesUrl: string): Promise<ModelReplaceRuleSet> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(rulesUrl, {
      cache: "no-cache",
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`ModelReplace rules request failed: ${response.status}`);
    }
    return parseRemoteRuleSet(await response.json());
  } finally {
    clearTimeout(timeout);
  }
}

async function requestOpenStatus(openStatusUrl: string): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(openStatusUrl, {
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`ModelReplace open-status request failed: ${response.status}`);
    }

    const payload = await response.json() as {
      data?: { is_open?: unknown };
    };
    const isOpen = payload.data?.is_open;
    if (isOpen === true || isOpen === 1 || isOpen === "1") return true;
    if (isOpen === false || isOpen === 0 || isOpen === "0") return false;
    throw new Error("ModelReplace open-status response is missing data.is_open");
  } finally {
    clearTimeout(timeout);
  }
}

function readCachedRuleSet(requestedUrl: string): CachedRuleSet | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const value = localStorage.getItem(RULES_CACHE_KEY);
    if (!value) return null;
    const cached = JSON.parse(value) as CachedRuleSet;
    if (
      cached.requestedUrl !== requestedUrl ||
      !Number.isFinite(cached.cachedAt)
    ) {
      return null;
    }
    return {
      requestedUrl,
      cachedAt: cached.cachedAt,
      ruleSet: normalizeRuleSet(cached.ruleSet),
    };
  } catch {
    return null;
  }
}

function writeCachedRuleSet(
  requestedUrl: string,
  ruleSet: ModelReplaceRuleSet,
): void {
  if (typeof localStorage === "undefined") return;
  try {
    const cached: CachedRuleSet = {
      requestedUrl,
      cachedAt: Date.now(),
      ruleSet,
    };
    localStorage.setItem(RULES_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Storage may be unavailable in privacy modes; memory rules still work.
  }
}

function resolveRemoteEndpoints(
  options: ModelReplaceStartOptions,
): RemoteEndpoints {
  const globalUrl =
    typeof window !== "undefined"
      ? (window as Window & { MODEL_REPLACE_BASE_URL?: string })
          .MODEL_REPLACE_BASE_URL
      : undefined;
  const baseUrl = options.baseUrl?.trim();
  const rulesUrl = options.rulesUrl?.trim();
  const legacyInjectedUrl =
    globalUrl?.trim() || __INJECTED_RULES_BASE_URL__.trim();
  const injectedUrl = baseUrl || rulesUrl || legacyInjectedUrl;
  if (!injectedUrl) {
    return {
      rulesUrl: DEFAULT_RULES_BASE_URL,
      openStatusUrl: DEFAULT_OPEN_STATUS_URL,
    };
  }

  try {
    const configuredUrl = new URL(injectedUrl);
    const configuredOrigin = configuredUrl.origin;
    let resolvedRulesUrl: string;
    if (rulesUrl) {
      resolvedRulesUrl = new URL(rulesUrl).toString();
    } else if (baseUrl) {
      resolvedRulesUrl = new URL(
        "/api/items/model_replace_v1?page=1&limit=10",
        configuredOrigin,
      ).toString();
    } else {
      const isBareBaseUrl =
        configuredUrl.pathname === "/" && !configuredUrl.search;
      resolvedRulesUrl = isBareBaseUrl
        ? new URL(
            "/api/items/model_replace_v1?page=1&limit=10",
            configuredOrigin,
          ).toString()
        : configuredUrl.toString();
    }

    return {
      rulesUrl: resolvedRulesUrl,
      openStatusUrl: new URL(
        "/api/items/open_model_replace/1",
        configuredOrigin,
      ).toString(),
    };
  } catch {
    return {
      rulesUrl: DEFAULT_RULES_BASE_URL,
      openStatusUrl: DEFAULT_OPEN_STATUS_URL,
    };
  }
}

async function checkOpenStatus(openStatusUrl: string): Promise<boolean> {
  const urls = openStatusUrl === DEFAULT_OPEN_STATUS_URL
    ? [DEFAULT_OPEN_STATUS_URL]
    : [openStatusUrl, DEFAULT_OPEN_STATUS_URL];
  let lastError: unknown;

  for (const url of urls) {
    try {
      return await requestOpenStatus(url);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

async function refreshRemoteRules(
  requestedUrl: string,
  generation: number,
  cacheTtlMs: number,
  debug: boolean,
): Promise<void> {
  const cached = readCachedRuleSet(requestedUrl);
  if (cached && generation === startGeneration) {
    runtime.updateRuleSet(cached.ruleSet);
    emit("updated", runtime.status());
    if (Date.now() - cached.cachedAt < cacheTtlMs) return;
  }

  const urls = requestedUrl === DEFAULT_RULES_BASE_URL
    ? [DEFAULT_RULES_BASE_URL]
    : [requestedUrl, DEFAULT_RULES_BASE_URL];
  let lastError: unknown;

  for (const url of urls) {
    try {
      const ruleSet = await requestRuleSet(url);
      if (generation !== startGeneration) return;
      runtime.updateRuleSet(ruleSet);
      writeCachedRuleSet(requestedUrl, ruleSet);
      emit("updated", runtime.status());
      return;
    } catch (error) {
      lastError = error;
    }
  }

  emit("error", lastError);
  if (debug) {
    console.error("[ModelReplace] remote rules unavailable", lastError);
  }
}

function emit(name: "ready" | "updated" | "error", detail: unknown): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(`modelreplace:${name}`, { detail }));
}

export async function start(
  options: ModelReplaceStartOptions = {},
): Promise<ModelReplaceStatus> {
  const generation = ++startGeneration;
  const initialRuleSet = options.ruleSet ?? embeddedRuleSet;
  runtime.configure(initialRuleSet, options);
  runtime.stop();
  runtime.setOpen(false);

  const endpoints = resolveRemoteEndpoints(options);
  let isOpen: boolean;
  try {
    isOpen = await checkOpenStatus(endpoints.openStatusUrl);
  } catch (error) {
    if (generation !== startGeneration) return runtime.status();
    emit("error", error);
    if (options.debug) {
      console.error("[ModelReplace] open-status unavailable", error);
    }
    const status = runtime.status();
    emit("ready", status);
    return status;
  }

  if (generation !== startGeneration) return runtime.status();
  runtime.setOpen(isOpen);
  if (!isOpen) {
    const status = runtime.status();
    emit("ready", status);
    return status;
  }

  runtime.start(options.root ?? document);
  const status = runtime.status();
  emit("ready", status);

  if (!options.ruleSet || options.rulesUrl) {
    const cacheTtlMs =
      typeof options.cacheTtlMs === "number" && options.cacheTtlMs >= 0
        ? options.cacheTtlMs
        : DEFAULT_CACHE_TTL_MS;
    void refreshRemoteRules(
      endpoints.rulesUrl,
      generation,
      cacheTtlMs,
      options.debug === true,
    );
  }

  return status;
}

export function stop(): void {
  startGeneration += 1;
  runtime.stop();
}

export function refresh(): number {
  return runtime.refresh();
}

export function replaceText(value: string): string {
  return runtime.replaceText(value);
}

export function getStatus(): ModelReplaceStatus {
  return runtime.status();
}

export const version = __PACKAGE_VERSION__;

interface AutoStartConfiguration extends ModelReplaceStartOptions {
  autoStart: boolean;
}

function readAutoStartConfiguration(): AutoStartConfiguration {
  const current = document.currentScript as HTMLScriptElement | null;
  const script = current ?? document.querySelector<HTMLScriptElement>("script[data-model-replace]");
  const dataset = script?.dataset;
  return {
    autoStart: dataset?.autoStart !== "false",
    baseUrl: dataset?.baseUrl,
    rulesUrl: dataset?.rulesUrl,
    observe: dataset?.observe !== "false",
    formControls: dataset?.formControls !== "false",
    ignoreSelector: dataset?.ignoreSelector,
    debug: dataset?.debug === "true",
  };
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const autoConfiguration = readAutoStartConfiguration();
  if (autoConfiguration.autoStart) {
    const run = () => void start(autoConfiguration);
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", run, { once: true });
    } else {
      run();
    }
  }
}
